// Compliance Auditor Page
// ============================================================
// Harsh critic auditor for automotive compliance
// Covers: AIAG-VDA, PPAP, IATF 16949
// Extensible for future standards
// Uses STATIC rules - NO LLM TOKENS CONSUMED
// ============================================================

import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../contexts/AppContext';
import {
    runComprehensiveAudit,
    ComprehensiveAuditResult,
    COMPLIANCE_STANDARDS,
    ComplianceStandard,
} from '../utils/complianceAuditor';
import {
    Shield,
    AlertTriangle,
    AlertCircle,
    CheckCircle,
    ChevronDown,
    ChevronUp,
    FileText,
    Download,
    RefreshCw,
    Target,
    Award,
    TrendingUp,
    Clock,
    Sparkles,
} from 'lucide-react';

// ============================================================
// SCORE DISPLAY COMPONENT
// ============================================================

interface ScoreGaugeProps {
    score: number;
    label: string;
    size?: 'sm' | 'md' | 'lg';
}

const ScoreGauge: React.FC<ScoreGaugeProps> = ({ score, label, size = 'md' }) => {
    const getColor = () => {
        if (score >= 80) return 'text-green-500';
        if (score >= 50) return 'text-amber-500';
        return 'text-red-500';
    };

    const getBgColor = () => {
        if (score >= 80) return 'bg-green-100 dark:bg-green-900/30';
        if (score >= 50) return 'bg-amber-100 dark:bg-amber-900/30';
        return 'bg-red-100 dark:bg-red-900/30';
    };

    const sizeClasses = {
        sm: 'w-16 h-16 text-lg',
        md: 'w-24 h-24 text-2xl',
        lg: 'w-32 h-32 text-4xl',
    };

    return (
        <div className="flex flex-col items-center">
            <div className={`${sizeClasses[size]} ${getBgColor()} rounded-full flex items-center justify-center border-4 ${getColor().replace('text-', 'border-')}`}>
                <span className={`font-bold ${getColor()}`}>{score}%</span>
            </div>
            <span className="text-sm text-[var(--text-secondary)] mt-2 text-center">{label}</span>
        </div>
    );
};

// ============================================================
// STATUS BADGE COMPONENT
// ============================================================

interface StatusBadgeProps {
    status: string;
    size?: 'sm' | 'md';
}

const StatusBadge: React.FC<StatusBadgeProps> = ({ status, size = 'md' }) => {
    const configs: Record<string, { bg: string; text: string; icon: React.ReactNode }> = {
        'compliant': { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-700 dark:text-green-400', icon: <CheckCircle size={14} /> },
        'conforming': { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-700 dark:text-green-400', icon: <CheckCircle size={14} /> },
        'complete': { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-700 dark:text-green-400', icon: <CheckCircle size={14} /> },
        'ready': { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-700 dark:text-green-400', icon: <CheckCircle size={14} /> },
        'needs-work': { bg: 'bg-amber-100 dark:bg-amber-900/30', text: 'text-amber-700 dark:text-amber-400', icon: <AlertTriangle size={14} /> },
        'partial': { bg: 'bg-amber-100 dark:bg-amber-900/30', text: 'text-amber-700 dark:text-amber-400', icon: <AlertTriangle size={14} /> },
        'in-progress': { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-700 dark:text-blue-400', icon: <Clock size={14} /> },
        'minor-nc': { bg: 'bg-amber-100 dark:bg-amber-900/30', text: 'text-amber-700 dark:text-amber-400', icon: <AlertTriangle size={14} /> },
        'non-compliant': { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-700 dark:text-red-400', icon: <AlertCircle size={14} /> },
        'major-nc': { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-700 dark:text-red-400', icon: <AlertCircle size={14} /> },
        'missing': { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-700 dark:text-red-400', icon: <AlertCircle size={14} /> },
        'not-ready': { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-700 dark:text-red-400', icon: <AlertCircle size={14} /> },
        'not-started': { bg: 'bg-gray-100 dark:bg-gray-800', text: 'text-gray-600 dark:text-gray-400', icon: <Clock size={14} /> },
    };

    const config = configs[status] || configs['not-started'];
    const sizeClass = size === 'sm' ? 'px-2 py-0.5 text-xs' : 'px-3 py-1 text-sm';

    return (
        <span className={`inline-flex items-center gap-1 ${sizeClass} ${config.bg} ${config.text} rounded-full font-medium`}>
            {config.icon}
            {status.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
        </span>
    );
};

// ============================================================
// EXPANDABLE SECTION COMPONENT
// ============================================================

interface ExpandableSectionProps {
    title: string;
    subtitle?: string;
    icon: React.ReactNode;
    status?: string;
    score?: number;
    defaultExpanded?: boolean;
    children: React.ReactNode;
}

const ExpandableSection: React.FC<ExpandableSectionProps> = ({
    title,
    subtitle,
    icon,
    status,
    score,
    defaultExpanded = false,
    children,
}) => {
    const [isExpanded, setIsExpanded] = useState(defaultExpanded);

    return (
        <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-xl overflow-hidden">
            <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="w-full flex items-center justify-between p-4 hover:bg-[var(--bg-tertiary)] transition-colors"
            >
                <div className="flex items-center gap-3">
                    <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg text-primary-600">
                        {icon}
                    </div>
                    <div className="text-left">
                        <h3 className="font-semibold text-[var(--text-primary)]">{title}</h3>
                        {subtitle && <p className="text-sm text-[var(--text-secondary)]">{subtitle}</p>}
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    {score !== undefined && (
                        <span className={`font-bold ${score >= 80 ? 'text-green-500' : score >= 50 ? 'text-amber-500' : 'text-red-500'}`}>
                            {score}%
                        </span>
                    )}
                    {status && <StatusBadge status={status} size="sm" />}
                    {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </div>
            </button>
            {isExpanded && (
                <div className="p-4 border-t border-[var(--border-color)]">
                    {children}
                </div>
            )}
        </div>
    );
};

// ============================================================
// PRINTABLE REPORT COMPONENT (Only visible in print)
// ============================================================

const PrintableReport: React.FC<{ result: ComprehensiveAuditResult }> = ({ result }) => {
    return (
        <div className="hidden print:block font-sans text-black bg-white p-8">
            {/* Header */}
            <div className="border-b-2 border-black pb-4 mb-8 flex justify-between items-center">
                <div>
                    <h1 className="text-3xl font-bold uppercase tracking-wider">Compliance Audit Report</h1>
                    <p className="text-gray-600 mt-1">Project: {result.projectName}</p>
                    <p className="text-gray-500 text-sm">Generated: {new Date().toLocaleDateString()} {new Date().toLocaleTimeString()}</p>
                </div>
                <div className="text-right">
                    <div className="text-4xl font-bold">{result.overallCompliance}%</div>
                    <div className="text-sm uppercase font-semibold text-gray-600">Compliance Score</div>
                </div>
            </div>

            {/* Executive Summary */}
            <section className="mb-8">
                <h2 className="text-xl font-bold border-b border-gray-300 mb-4 pb-2">1. Executive Summary</h2>
                <div className="bg-gray-50 p-4 rounded border border-gray-200">
                    <p className="text-lg leading-relaxed mb-4">{result.executiveSummary}</p>
                    <div className="grid grid-cols-3 gap-4 mt-4">
                        <div className="text-center p-2 bg-white border rounded">
                            <div className="font-bold">AIAG-VDA</div>
                            <div className="text-xl">{result.aiagVda.overallScore}%</div>
                        </div>
                        <div className="text-center p-2 bg-white border rounded">
                            <div className="font-bold">PPAP</div>
                            <div className="text-xl">{result.ppap.overallReadiness}%</div>
                        </div>
                        <div className="text-center p-2 bg-white border rounded">
                            <div className="font-bold">IATF 16949</div>
                            <div className="text-xl">{result.iatf16949.overallScore}%</div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Prioritized Recommendations */}
            <section className="mb-8 keep-together">
                <h2 className="text-xl font-bold border-b border-gray-300 mb-4 pb-2">2. Prioritized Action Plan</h2>

                {result.criticalActions.length > 0 && (
                    <div className="mb-6">
                        <h3 className="font-bold text-red-700 mb-2 uppercase text-sm">Critical Actions (Immediate Attention)</h3>
                        <ul className="list-disc pl-5 space-y-1">
                            {result.criticalActions.map((action, i) => (
                                <li key={i} className="text-red-800 font-medium">{action}</li>
                            ))}
                        </ul>
                    </div>
                )}

                {result.highPriorityActions.length > 0 && (
                    <div className="mb-6">
                        <h3 className="font-bold text-amber-700 mb-2 uppercase text-sm">High Priority (Before Submission)</h3>
                        <ul className="list-disc pl-5 space-y-1">
                            {result.highPriorityActions.map((action, i) => (
                                <li key={i} className="text-gray-900">{action}</li>
                            ))}
                        </ul>
                    </div>
                )}

                {result.improvements.length > 0 && (
                    <div>
                        <h3 className="font-bold text-blue-700 mb-2 uppercase text-sm">Recommended Improvements</h3>
                        <ul className="list-disc pl-5 space-y-1">
                            {result.improvements.map((action, i) => (
                                <li key={i} className="text-gray-700">{action}</li>
                            ))}
                        </ul>
                    </div>
                )}
            </section>

            {/* PPAP Checklist */}
            <section className="mb-8 keep-together">
                <h2 className="text-xl font-bold border-b border-gray-300 mb-4 pb-2">3. PPAP Readiness Checklist</h2>
                <table className="w-full text-sm border-collapse">
                    <thead>
                        <tr className="bg-gray-100 border-b border-gray-300 text-left">
                            <th className="py-2 px-2 w-16">#</th>
                            <th className="py-2 px-2">Element</th>
                            <th className="py-2 px-2">Findings</th>
                            <th className="py-2 px-2 w-24">Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        {result.ppap.elements.map((el) => (
                            <tr key={el.number} className="border-b border-gray-200">
                                <td className="py-2 px-2 font-bold">{el.number}</td>
                                <td className="py-2 px-2 font-medium">{el.element}</td>
                                <td className="py-2 px-2 text-gray-600">
                                    {el.findings.length > 0 ? el.findings.join('; ') : el.recommendations.join('; ')}
                                </td>
                                <td className="py-2 px-2">
                                    <span className={`px-2 py-1 rounded text-xs uppercase font-bold ${el.status === 'complete' ? 'bg-green-100 text-green-800' :
                                        el.status === 'partial' ? 'bg-yellow-100 text-yellow-800' :
                                            'bg-red-100 text-red-800'
                                        }`}>
                                        {el.status}
                                    </span>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </section>

            {/* Detailed Findings */}
            <section className="keep-together">
                <h2 className="text-xl font-bold border-b border-gray-300 mb-4 pb-2">4. Detailed Audit Findings</h2>

                <h3 className="font-bold text-lg mb-2 mt-4 text-gray-700">AIAG-VDA 7-Step Analysis</h3>
                <div className="grid grid-cols-2 gap-4 mb-4">
                    {result.aiagVda.stepResults.map(step => (
                        <div key={step.step} className="border p-3 rounded bg-white">
                            <div className="flex justify-between mb-1">
                                <span className="font-bold">Step {step.step}: {step.stepName}</span>
                                <span className={step.score >= 80 ? 'text-green-700' : 'text-red-700'}>{step.score}%</span>
                            </div>
                            <div className="text-xs text-gray-500">
                                {step.completedItems} / {step.requiredItems} items complete
                            </div>
                        </div>
                    ))}
                </div>

                <h3 className="font-bold text-lg mb-2 mt-6 text-gray-700">IATF 16949 Gaps</h3>
                <div className="space-y-2">
                    {result.iatf16949.clauses.filter(c => c.status !== 'conforming').map(clause => (
                        <div key={clause.number} className="border-l-4 border-red-500 pl-3 py-1">
                            <span className="font-bold text-gray-900 block">{clause.number} - {clause.clause}</span>
                            <span className="text-red-700 text-sm block uppercase text-xs font-bold mb-1">{clause.status}</span>
                            <ul className="list-disc pl-4 text-sm text-gray-700">
                                {clause.opportunities.map((opp, i) => <li key={i}>{opp}</li>)}
                            </ul>
                        </div>
                    ))}
                    {result.iatf16949.clauses.every(c => c.status === 'conforming') && (
                        <p className="text-green-700 italic">No non-conformances found against audited IATF clauses.</p>
                    )}
                </div>
            </section>
        </div>
    );
};

// ============================================================
// MAIN AUDITOR PAGE
// ============================================================

const ComplianceAuditorPage: React.FC = () => {
    const navigate = useNavigate();
    const {
        activeProject,
        activeProjectName,
        pfmeaItems,
        processFlow,
        controlPlans,
        scoping,
        processFunctions,
        productRequirements,
    } = useAppContext();

    const [isRefreshing, setIsRefreshing] = useState(false);

    // Run comprehensive audit
    const auditResult = useMemo<ComprehensiveAuditResult | null>(() => {
        if (!activeProjectName) return null;
        return runComprehensiveAudit(
            activeProjectName,
            pfmeaItems,
            processFlow,
            controlPlans,
            scoping,
            processFunctions,
            productRequirements
        );
    }, [activeProjectName, pfmeaItems, processFlow, controlPlans, scoping, processFunctions, productRequirements]);

    const handleRefresh = () => {
        setIsRefreshing(true);
        setTimeout(() => setIsRefreshing(false), 500);
    };

    const handleExport = () => {
        window.print();
    };

    // No project selected
    if (!activeProject || activeProject === 'Select Project') {
        return (
            <div className="flex flex-col items-center justify-center h-96 text-center">
                <Shield size={64} className="text-slate-300 mb-4" />
                <h2 className="text-xl font-semibold mb-2 text-[var(--text-primary)]">
                    No Project Selected
                </h2>
                <p className="text-[var(--text-secondary)] mb-4">
                    Select a project to run the compliance audit.
                </p>
                <button
                    onClick={() => navigate('/projects')}
                    className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600"
                >
                    Go to Projects
                </button>
            </div>
        );
    }

    if (!auditResult) {
        return (
            <div className="flex items-center justify-center h-96">
                <div className="animate-spin text-primary-500">
                    <RefreshCw size={32} />
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="p-6 space-y-6 print:hidden">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold text-[var(--text-primary)] flex items-center gap-2">
                            <Shield className="text-primary-500" />
                            Compliance Auditor
                        </h1>
                        <p className="text-[var(--text-secondary)]">
                            Harsh critic analysis for automotive certification readiness
                        </p>
                    </div>
                    <div className="flex items-center gap-3">
                        <span className="px-3 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-full text-sm font-medium">
                            ✓ No AI tokens used - Static rules only
                        </span>
                        <button
                            onClick={handleRefresh}
                            className={`p-2 rounded-lg hover:bg-[var(--bg-tertiary)] transition-colors ${isRefreshing ? 'animate-spin' : ''}`}
                        >
                            <RefreshCw size={20} className="text-[var(--text-secondary)]" />
                        </button>
                        <button
                            onClick={handleExport}
                            className="flex items-center gap-2 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600"
                        >
                            <Download size={18} />
                            Print Report
                        </button>
                    </div>
                </div>

                {/* Executive Summary Card */}
                <div className={`rounded-2xl p-6 ${auditResult.overallCompliance >= 80
                    ? 'bg-gradient-to-r from-green-600 to-emerald-600'
                    : auditResult.overallCompliance >= 50
                        ? 'bg-gradient-to-r from-amber-600 to-orange-600'
                        : 'bg-gradient-to-r from-red-600 to-rose-600'
                    } text-white`}>
                    <div className="flex items-start justify-between">
                        <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                                {auditResult.certificationReady ? (
                                    <Award className="text-yellow-300" size={24} />
                                ) : (
                                    <Target size={24} />
                                )}
                                <h2 className="text-xl font-bold">
                                    {auditResult.certificationReady
                                        ? 'Certification Ready!'
                                        : 'Certification Path'}
                                </h2>
                            </div>
                            <p className="text-white/90 mb-4">{auditResult.executiveSummary}</p>

                            {/* Next Steps */}
                            {auditResult.nextSteps.length > 0 && (
                                <div className="mt-4">
                                    <p className="font-semibold mb-2">Next Steps:</p>
                                    <ul className="space-y-1">
                                        {auditResult.nextSteps.slice(0, 3).map((step, idx) => (
                                            <li key={idx} className="flex items-start gap-2 text-sm text-white/90">
                                                <span className="text-yellow-300">→</span>
                                                {step}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>

                        {/* Overall Score */}
                        <div className="text-center ml-6">
                            <div className="w-32 h-32 bg-white/20 rounded-full flex items-center justify-center border-4 border-white/40">
                                <span className="text-4xl font-bold">{auditResult.overallCompliance}%</span>
                            </div>
                            <p className="text-sm mt-2 text-white/80">Overall Compliance</p>
                        </div>
                    </div>
                </div>

                {/* Three Standards Overview */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* AIAG-VDA */}
                    <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-xl p-4">
                        <div className="flex items-center gap-3 mb-3">
                            <span className="text-2xl">🚗</span>
                            <div>
                                <h3 className="font-semibold text-[var(--text-primary)]">AIAG-VDA</h3>
                                <p className="text-xs text-[var(--text-secondary)]">FMEA Methodology</p>
                            </div>
                        </div>
                        <ScoreGauge score={auditResult.aiagVda.overallScore} label="7-Step Compliance" size="sm" />
                        <div className="mt-3 text-sm">
                            <p className="text-[var(--text-secondary)]">
                                {auditResult.aiagVda.stepResults.filter(s => s.status === 'compliant').length} of 7 steps complete
                            </p>
                        </div>
                    </div>

                    {/* PPAP */}
                    <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-xl p-4">
                        <div className="flex items-center gap-3 mb-3">
                            <span className="text-2xl">📋</span>
                            <div>
                                <h3 className="font-semibold text-[var(--text-primary)]">PPAP</h3>
                                <p className="text-xs text-[var(--text-secondary)]">Part Approval</p>
                            </div>
                        </div>
                        <ScoreGauge score={auditResult.ppap.overallReadiness} label="Submission Readiness" size="sm" />
                        <div className="mt-3 text-sm">
                            <p className="text-[var(--text-secondary)]">
                                Level {auditResult.ppap.submissionLevel} submission
                            </p>
                        </div>
                    </div>

                    {/* IATF 16949 */}
                    <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-xl p-4">
                        <div className="flex items-center gap-3 mb-3">
                            <span className="text-2xl">🏭</span>
                            <div>
                                <h3 className="font-semibold text-[var(--text-primary)]">IATF 16949</h3>
                                <p className="text-xs text-[var(--text-secondary)]">Quality System</p>
                            </div>
                        </div>
                        <ScoreGauge score={auditResult.iatf16949.overallScore} label="Clause Conformance" size="sm" />
                        <div className="mt-3 text-sm">
                            <StatusBadge status={auditResult.iatf16949.status} size="sm" />
                        </div>
                    </div>
                </div>

                {/* Critical Actions */}
                {auditResult.criticalActions.length > 0 && (
                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4">
                        <div className="flex items-center gap-2 mb-3">
                            <AlertCircle className="text-red-500" size={20} />
                            <h3 className="font-semibold text-red-700 dark:text-red-400">
                                Critical Actions Required ({auditResult.criticalActions.length})
                            </h3>
                        </div>
                        <ul className="space-y-2">
                            {auditResult.criticalActions.map((action, idx) => (
                                <li key={idx} className="flex items-start gap-2 text-sm text-red-700 dark:text-red-300">
                                    <span className="font-bold text-red-500">!</span>
                                    {action}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {/* Detailed Results */}
                <div className="space-y-4">
                    {/* AIAG-VDA Details */}
                    <ExpandableSection
                        title="AIAG-VDA FMEA Analysis"
                        subtitle="7-Step methodology compliance"
                        icon={<FileText size={20} />}
                        score={auditResult.aiagVda.overallScore}
                        status={auditResult.aiagVda.status}
                        defaultExpanded={true}
                    >
                        <div className="space-y-4">
                            {/* Step Progress */}
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                                {auditResult.aiagVda.stepResults.map((step) => (
                                    <div
                                        key={step.step}
                                        className="bg-[var(--bg-tertiary)] rounded-lg p-3"
                                    >
                                        <div className="flex items-center justify-between mb-2">
                                            <span className="text-sm font-medium text-[var(--text-primary)]">
                                                Step {step.step}
                                            </span>
                                            <StatusBadge status={step.status} size="sm" />
                                        </div>
                                        <p className="text-xs text-[var(--text-secondary)] mb-2">{step.stepName}</p>
                                        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                                            <div
                                                className={`h-2 rounded-full ${step.score >= 80 ? 'bg-green-500' :
                                                    step.score >= 50 ? 'bg-amber-500' : 'bg-red-500'
                                                    }`}
                                                style={{ width: `${step.score}%` }}
                                            />
                                        </div>
                                        <p className="text-xs text-[var(--text-secondary)] mt-1">
                                            {step.completedItems}/{step.requiredItems} items
                                        </p>
                                    </div>
                                ))}
                            </div>

                            {/* Findings */}
                            {auditResult.aiagVda.criticalFindings.length > 0 && (
                                <div className="mt-4 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
                                    <h4 className="font-medium text-red-700 dark:text-red-400 mb-2">
                                        Critical Findings
                                    </h4>
                                    <div className="space-y-2">
                                        {auditResult.aiagVda.criticalFindings.slice(0, 5).map((finding) => (
                                            <div key={finding.id} className="text-sm">
                                                <p className="font-medium text-red-600 dark:text-red-400">
                                                    {finding.category}: {finding.finding}
                                                </p>
                                                <p className="text-red-500/80 dark:text-red-300/80 text-xs mt-1">
                                                    💡 Auditor Tip: {finding.auditorTip}
                                                </p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </ExpandableSection>

                    {/* PPAP Details */}
                    <ExpandableSection
                        title="PPAP Element Analysis"
                        subtitle="Production Part Approval Process readiness"
                        icon={<Target size={20} />}
                        score={auditResult.ppap.overallReadiness}
                        status={auditResult.ppap.status}
                    >
                        <div className="space-y-3">
                            {auditResult.ppap.elements.map((element) => (
                                <div
                                    key={element.number}
                                    className="flex items-center justify-between p-3 bg-[var(--bg-tertiary)] rounded-lg"
                                >
                                    <div className="flex items-center gap-3">
                                        <span className="text-lg font-bold text-[var(--text-secondary)] w-8">
                                            {element.number}
                                        </span>
                                        <div>
                                            <p className="font-medium text-[var(--text-primary)]">{element.element}</p>
                                            <p className="text-xs text-[var(--text-secondary)]">{element.description}</p>
                                        </div>
                                    </div>
                                    <StatusBadge status={element.status} size="sm" />
                                </div>
                            ))}
                        </div>
                    </ExpandableSection>

                    {/* IATF 16949 Details */}
                    <ExpandableSection
                        title="IATF 16949 Clause Analysis"
                        subtitle="Quality Management System requirements"
                        icon={<Award size={20} />}
                        score={auditResult.iatf16949.overallScore}
                        status={auditResult.iatf16949.status}
                    >
                        <div className="space-y-3">
                            {auditResult.iatf16949.clauses.map((clause) => (
                                <div
                                    key={clause.number}
                                    className="p-3 bg-[var(--bg-tertiary)] rounded-lg"
                                >
                                    <div className="flex items-center justify-between mb-2">
                                        <div className="flex items-center gap-3">
                                            <span className="text-sm font-bold text-primary-500">
                                                {clause.number}
                                            </span>
                                            <p className="font-medium text-[var(--text-primary)]">{clause.clause}</p>
                                        </div>
                                        <StatusBadge status={clause.status} size="sm" />
                                    </div>
                                    <p className="text-xs text-[var(--text-secondary)] mb-2">{clause.description}</p>
                                    {clause.findings.length > 0 && (
                                        <p className="text-xs text-green-600 dark:text-green-400">
                                            ✓ {clause.findings[0]}
                                        </p>
                                    )}
                                    {clause.opportunities.length > 0 && (
                                        <p className="text-xs text-amber-600 dark:text-amber-400">
                                            💡 {clause.opportunities[0]}
                                        </p>
                                    )}
                                </div>
                            ))}
                        </div>
                    </ExpandableSection>
                </div>

                {/* AI Assistant Prompt */}
                <div className="bg-gradient-to-r from-purple-100 to-blue-100 dark:from-purple-900/30 dark:to-blue-900/30 rounded-xl p-6 border border-purple-200 dark:border-purple-800">
                    <div className="flex items-start gap-4">
                        <div className="p-3 bg-purple-500 rounded-xl">
                            <Sparkles className="text-white" size={24} />
                        </div>
                        <div className="flex-1">
                            <h3 className="font-semibold text-[var(--text-primary)] mb-2">
                                Need Help Improving Compliance?
                            </h3>
                            <p className="text-[var(--text-secondary)] mb-4">
                                Ask Magnus AI for specific guidance on closing gaps. Use Prime mode for detailed analysis.
                            </p>
                            <div className="flex flex-wrap gap-2">
                                <button
                                    onClick={() => navigate('/ai-assistant')}
                                    className="flex items-center gap-2 px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600"
                                >
                                    <Sparkles size={16} />
                                    Ask Magnus AI
                                </button>
                                <button
                                    onClick={() => navigate('/ideation')}
                                    className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-slate-800 border border-purple-300 dark:border-purple-700 text-purple-600 dark:text-purple-400 rounded-lg hover:bg-purple-50 dark:hover:bg-purple-900/20"
                                >
                                    <TrendingUp size={16} />
                                    Generate Missing Content
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Future Standards Preview */}
                <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-xl p-4">
                    <h3 className="font-semibold text-[var(--text-primary)] mb-3">
                        Coming Soon: Additional Standards
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {(['ISO-14971', 'ISO-26262', 'IEC-61508', 'MIL-STD-1629A'] as ComplianceStandard[]).map(std => {
                            const standard = COMPLIANCE_STANDARDS[std];
                            return (
                                <div
                                    key={std}
                                    className="p-3 bg-[var(--bg-tertiary)] rounded-lg opacity-60"
                                >
                                    <div className="flex items-center gap-2 mb-1">
                                        <span>{standard.icon}</span>
                                        <span className="font-medium text-sm text-[var(--text-primary)]">
                                            {standard.name}
                                        </span>
                                    </div>
                                    <p className="text-xs text-[var(--text-secondary)]">{standard.industry}</p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            <PrintableReport result={auditResult} />
        </>
    );
};

export default ComplianceAuditorPage;
